/**
 * The development-only automatic-stop logger (`createVadFrameLog`) keeps recent
 * microphone measurements for investigating premature stops. It returns null
 * outside development.
 */
import { SPECTRUM_BAND_EDGES_HZ } from './voice-activity-detector';
import { type VoiceActivityDetectorOptions } from './voice-activity-options';

/** How much recent microphone analysis to keep for diagnosing an early stop */
const VAD_FRAME_LOG_WINDOW_MS = 10_000;
/**
 * Silence this long is logged if speech resumes before automatic stopping.
 * These near misses help distinguish risky pauses from ordinary gaps between
 * words.
 */
const VAD_NEAR_MISS_SILENCE_MS = 800;

const roundForLog = (value: number, fractionDigits: number) =>
  Number(value.toFixed(fractionDigits));

/** One detector input plus diagnostics not needed by the stopping policy */
type VadLogFrame = {
  /** Wall-clock sample time */
  nowMs: number;
  /** Normalized waveform volume */
  rms: number;
  /** Share of frequency energy inside the configured speech band */
  voiceScore: number;
  /** Energy shares for the diagnostic bands in `SPECTRUM_BAND_EDGES_HZ` */
  bandRatios: number[];
  /** Start of the current post-speech silence run, if any */
  trailingSilenceStartedAtMs: number | null;
};

/** Rounded detector measurement retained in the development event log */
type StoredVadLogFrame = {
  atMs: number;
  rms: number;
  voiceScore: number;
  bands: number[];
};

/** Development diagnostics collected while one automatic recording is active */
type VadFrameLog = {
  onFrame(frame: VadLogFrame): void;
  onStopAfterSpeech(nowMs: number): void;
};

/**
 * Keep recent VAD frames in development so premature stops can be reconstructed
 */
export function createVadFrameLog(
  vadOptions: VoiceActivityDetectorOptions,
  logEvent: (event: string, metadata?: Record<string, unknown>) => void
): VadFrameLog | null {
  if (!import.meta.env.DEV || import.meta.env.MODE === 'test') return null;

  const frameLogCapacity = Math.ceil(
    VAD_FRAME_LOG_WINDOW_MS / vadOptions.frameMs
  );
  const recentFrames: StoredVadLogFrame[] = [];
  let activeTrailingSilenceStartedAtMs: number | null = null;

  return {
    onFrame({
      nowMs,
      rms,
      voiceScore,
      bandRatios,
      trailingSilenceStartedAtMs,
    }: VadLogFrame) {
      const bands = bandRatios.map((ratio) => roundForLog(ratio, 3));
      recentFrames.push({
        atMs: nowMs,
        rms: roundForLog(rms, 4),
        voiceScore: roundForLog(voiceScore, 4),
        bands,
      });
      if (recentFrames.length > frameLogCapacity) recentFrames.shift();

      if (trailingSilenceStartedAtMs !== null) {
        if (trailingSilenceStartedAtMs === activeTrailingSilenceStartedAtMs)
          return;
        activeTrailingSilenceStartedAtMs = trailingSilenceStartedAtMs;
        logEvent('vad-trailing-silence-started', {
          trailingSilenceMs: vadOptions.trailingSilenceMs,
          trailingSilenceStartedAt: new Date(
            trailingSilenceStartedAtMs
          ).toISOString(),
          trailingSilenceStartedAtMs,
          rms: roundForLog(rms, 4),
          voiceScore: roundForLog(voiceScore, 4),
          bands,
        });
        return;
      }

      if (activeTrailingSilenceStartedAtMs === null) return;
      const silenceRunMs = nowMs - activeTrailingSilenceStartedAtMs;
      activeTrailingSilenceStartedAtMs = null;
      // A near miss is a long pause followed by more speech rather than a stop
      if (silenceRunMs < VAD_NEAR_MISS_SILENCE_MS) return;
      logEvent('vad-trailing-silence-ended', {
        silenceRunMs,
        trailingSilenceMs: vadOptions.trailingSilenceMs,
        rms: roundForLog(rms, 4),
        voiceScore: roundForLog(voiceScore, 4),
        bands,
      });
    },
    onStopAfterSpeech(nowMs: number) {
      logEvent('vad-stop-after-speech', {
        trailingSilenceMs: vadOptions.trailingSilenceMs,
        speechRmsThreshold: vadOptions.speechRmsThreshold,
        minimumVoiceScore: vadOptions.minimumVoiceScore,
        voiceFrequencyRangeHz: [
          vadOptions.minimumVoiceFrequencyHz,
          vadOptions.maximumVoiceFrequencyHz,
        ],
        bandEdgesHz: SPECTRUM_BAND_EDGES_HZ,
        trailingSilenceStartedAtMs: activeTrailingSilenceStartedAtMs,
        elapsedSinceTrailingSilenceStartedMs:
          activeTrailingSilenceStartedAtMs === null
            ? undefined
            : nowMs - activeTrailingSilenceStartedAtMs,
        recentFrames,
      });
    },
  };
}
